// "Retrieve to cart" for parked bills on the Fashion layout.
//
// Core's selectParkedBill (useParkedBills) always opens payment: it calls
// setShowPaymentMethods(true), then removes the bill from parkedList and puts it
// on the cart, all in one synchronous block. Core is shared, so instead of
// changing it this watches the session store for the retrieved bill leaving
// parkedList and turns payment back off in that same block. React batches both
// updates, so payment never shows and the cart (lines, discounts, customer,
// taxes) is exactly what Core put there.
//
// Opt-in: only retrieveParked(bill, { retrieveTo: "cart" }) does this. The
// default ("payment") is today's behaviour.
import useSessionStore from "../../stores/useSessionStore";

// A retrieve that never finishes (stock check failed, the "park the current
// bill?" prompt was declined) stops waiting after this long.
export const RETRIEVE_TO_CART_TTL = 2 * 60 * 1000;

let pending = null;
let unsubscribe = null;

const sameBill = (bill, p) => (p.id != null ? bill?.parkedBillId === p.id : p.docNo != null && bill?.parkedDocNo === p.docNo);

// True once the pending bill is no longer parked, i.e. Core has retrieved it.
export function retrievedBillLeftList(p, parkedList) {
  if (!p) return false;
  return !(parkedList || []).some((bill) => sameBill(bill, p));
}

export function clearRetrieveToCart() {
  pending = null;
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
}

export function pendingRetrieveToCart(now = Date.now()) {
  if (pending && now - pending.at > RETRIEVE_TO_CART_TTL) clearRetrieveToCart();
  return pending;
}

function onParkedListChange(parkedList) {
  const p = pendingRetrieveToCart();
  if (!p || !retrievedBillLeftList(p, parkedList)) return;
  const close = p.setShowPaymentMethods;
  clearRetrieveToCart();
  close(false);
}

export function markRetrieveToCart(bill, setShowPaymentMethods, now = Date.now()) {
  clearRetrieveToCart();
  pending = { id: bill?.parkedBillId ?? null, docNo: bill?.parkedDocNo ?? null, at: now, setShowPaymentMethods };
  unsubscribe = useSessionStore.subscribe((state, prev) => {
    if (state.parkedList !== prev?.parkedList) onParkedListChange(state.parkedList);
  });
  return pending;
}

// Whether this layout can offer "Retrieve to cart" at all.
export const canRetrieveToCart = ({ retrieveParkedData, setShowPaymentMethods }) => typeof retrieveParkedData === "function" && typeof setShowPaymentMethods === "function";

// retrieveTo "payment" (default): Core's retrieve, unchanged — opens payment.
// retrieveTo "cart": same retrieve, then payment is turned off once the bill is on the cart.
export function retrieveParked(bill, { retrieveTo = "payment", retrieveParkedData, setShowPaymentMethods } = {}) {
  if (typeof retrieveParkedData !== "function") return false;
  if (retrieveTo === "cart" && typeof setShowPaymentMethods === "function") markRetrieveToCart(bill, setShowPaymentMethods);
  else clearRetrieveToCart();
  retrieveParkedData(bill);
  return true;
}
